import React, { useEffect, useState, useRef } from 'react';
import { FieldOption, SearchFieldType } from 'types/FieldTypes';
import { useTranslation } from 'react-i18next';
import { useClickOutside, useSetDropdownWidth, calculateDropdownPosition } from 'services/utils/dropdownBehavior'
import { useFetchSearchHook } from 'services/api/fetchSearchHook'; 
import '../../../style/scss/forms.scss';

const SearchField = <T extends { id?: number; [key: string]: any }>({
    name, placeholder, query, autoFocus, onSelect, optionFormat
}: SearchFieldType<T>) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const { options, loading, fetchFinished, resetLastFetchValue } = useFetchSearchHook(query.endpoint, query.params, search);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dropdownListRef = useRef<HTMLDivElement>(null);
    const fieldRef = useRef<HTMLInputElement>(null);
    const [dropdownWidth, setDropdownWidth] = useState<number>(200);
    const [dropdownAbove, setDropdownAbove] = useState(false);
    const [dropdownMaxHeight, setDropdownMaxHeight] = useState<number | undefined>(undefined);

    // Helper function that gives the value of an object by it's path
    function getValueByPath(obj: any, path: string) {
        const parts = path.split(/[.\[\]]/).filter((part: string) => part.length);
        return parts.reduce((acc: any, part: string) => acc && acc[part], obj);
    }

    // Format the text in the selection list options
    function formatOption(option: any) {
        let title = '';
        let bracket = '';
        let subtitle = '';

        if (optionFormat) {
            // Format the title
            if (optionFormat.title) {
                const { field, format } = optionFormat.title;
                const value = getValueByPath(option, field);
                title = format(value);
            }

            // Format the optional text between the brackets
            if (optionFormat.bracket) {
                const { field, format, optional } = optionFormat.bracket;
                const value = getValueByPath(option, field);
                if (value || !optional) {
                    bracket = `(${format(value)})`;
                }
            }

            // Format the optional subtitle
            if (optionFormat.subtitle) {
                const { field, format, optional } = optionFormat.subtitle;
                const value = getValueByPath(option, field);
                if (value || !optional) {
                    subtitle = format(value);
                }
            }
        // Set the title in case no option format is given
        } else {
            title = option.name || '';
        }

        // Render the format of the option
        return (
            <React.Fragment>
                <div>{title} {bracket}</div>
                {subtitle && <div className='subtitle'>{subtitle}</div>}
            </React.Fragment>
        );
    }

    // Close the dropdown list when the searchfield is made empty
    useEffect(() => {
        if (search === '') {
            setIsOpen(false);
        }
    }, [search]);

    // Use dropdownBehavior.ts to close the dropdown when clicking outside
    useClickOutside(isOpen, dropdownRef, setIsOpen);

    // Use dropdownBehavior.ts to set the dropdown width on open
    useSetDropdownWidth(fieldRef, setDropdownWidth);

    // Handle the opening of the dropdown
    const openDropdown = () => {
        if (!fieldRef.current) return;

        // Calculate the dropdown position
        calculateDropdownPosition(fieldRef, setDropdownAbove, setDropdownMaxHeight);

        // Open the dropdown
        setIsOpen(true);
    };

    return (
        <div className='searchselect-container'>
            <input
                type="text"
                name={name}
                ref={fieldRef}
                value={search}
                onChange={(e) => {setSearch(e.target.value); openDropdown()}}
                placeholder={t(placeholder)}
                autoFocus={autoFocus}
            />
            {loading && <div className="loader"></div>}
            {isOpen && !loading && fetchFinished && (
                <div className={`custom-dropdown ${dropdownAbove ? 'dropdown-above' : ''}`} 
                     ref={dropdownRef} 
                     style={{width: (dropdownWidth - 2), maxHeight: dropdownMaxHeight}}>
                    <div className='custom-dropdown-list' style={{width: dropdownWidth}}>
                        <div className='searchselect-options' ref={dropdownListRef}>
                            {options.map((option: T, index) => {
                                return (
                                    <div key={index} 
                                         className='custom-dropdown-item'
                                         onClick={() => {
                                            if (onSelect) {
                                                onSelect(option)
                                            }
                                         }}>
                                            {formatOption(option)}
                                    </div>
                                )
                            })}
                            {options.length >= 50 && 
                                <div className="limit-message">
                                    {t('forms.dropdown_option_limit_message')}
                                </div>
                            }
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchField;